import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getMergedEnvironmentConfig,
  shouldShowEnvironmentInfoFromConfig,
} from "../../environment/environmentConfig";
import { commit } from "../../_git_commit";
import "./EnvironmentInfo.css";

const DIGIT_ONE_CODES = new Set(["Digit1", "Numpad1"]);

function useKeyboardVisibilityToggle(configAllowsBaseVisibility) {
  const [keyboardDismissed, setKeyboardDismissed] = useState(false);
  const [keyboardForced, setKeyboardForced] = useState(false);

  const stateRef = useRef({
    dismissed: false,
    forced: false,
    configAllows: true,
  });
  stateRef.current.dismissed = keyboardDismissed;
  stateRef.current.forced = keyboardForced;
  stateRef.current.configAllows = configAllowsBaseVisibility;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      if (e.key !== "1" && !DIGIT_ONE_CODES.has(e.code)) return;
      e.preventDefault();

      const { dismissed, forced, configAllows } = stateRef.current;
      const visibleNow = forced || (!dismissed && configAllows);
      if (visibleNow) {
        setKeyboardForced(false);
        setKeyboardDismissed(true);
      } else {
        setKeyboardDismissed(false);
        setKeyboardForced(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return keyboardForced || (!keyboardDismissed && configAllowsBaseVisibility);
}

export default function EnvironmentInfo() {
  const config = useMemo(() => getMergedEnvironmentConfig(), []);
  const configAllows = shouldShowEnvironmentInfoFromConfig(
    config.showEnvironmentInfo
  );

  const visible = useKeyboardVisibilityToggle(configAllows);

  const [allowHover, setAllowHover] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setAllowHover(true), 5000);
    return () => window.clearTimeout(t);
  }, []);

  if (!visible) return null;

  const cardStyle = {
    backgroundColor: config.color,
  };

  return (
    <div
      className={`meta${allowHover ? " meta--interactive" : ""}`}
      data-environment-info=""
    >
      <div
        className={`environment-info${allowHover ? " allow-hover" : ""}`}
        style={cardStyle}
      >
        <div className="environment">
          {config.name}
          <span className="environment-info-badge">
            {config.appDefaultBehavior}
          </span>
        </div>
        <div className="version">
          Build version {config.appVersion}{" "}
          {commit && commit !== "unknown" ? `(Commit ${commit})` : ""}
        </div>
        <div className="env-info">
          <div className="env">API: {config.apiBaseUrl || "—"}</div>
        </div>
      </div>
    </div>
  );
}
